import { useLocation } from 'react-router-dom';

import { useActivation } from '../ActivationContext';

import { isAllowedAppHandoffTarget } from '@/utils/urlHelpers';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { logError } from '@/utils/logError';

/**
 * Where to send the member's device after sign-in. Looked up (this URL, then the saved copy, then
 * flow state) because an emailed link opens a fresh tab. Candidates must pass
 * `isAllowedAppHandoffTarget`; rejects are logged. No default address.
 */

interface RedirectToSources {
  /** The current screen's query string, e.g. `useLocation().search`. */
  search: string;
  /** Whatever the activation flow's state still holds, when a caller has that state. */
  fromFlowState?: string | null;
}

/**
 * The scheme of a value, for logging. Never log the value itself — a rejected address can
 * carry arbitrary query params, and this runs on paths a member's own data passes through.
 */
const schemeOf = (value: string): string => {
  const idx = value.indexOf(':');
  return idx === -1 ? '(none)' : value.slice(0, idx + 1);
};

/**
 * Values already reported. These resolvers are called from render, so without this a single
 * screen would log once per frame. Page-scoped and bounded by the number of distinct bad
 * values a page sees, which is one in every case we know of.
 */
const reportedRejections = new Set<string>();

const reportRejected = (field: string, value: string): void => {
  const seen = `${field}:${value}`;
  if (reportedRejections.has(seen)) { return; }
  reportedRejections.add(seen);
  logError(
    '[PostAuthRedirect]',
    'ignored a redirect address that is not one of the app handoff targets',
    undefined,
    { level: 'warning', extra: { field, scheme: schemeOf(value) } },
  );
};

/**
 * The address the app actually asked for, or nothing at all.
 *
 * Returning nothing matters when we relay this to the auth service: claiming an app
 * address for a member who is signing in on a desktop browser would put a private app
 * link in their email that their machine cannot open. Absent means absent.
 */
export const appSuppliedRedirectTo = (
  { search, fromFlowState }: RedirectToSources,
): string | undefined => {
  const fromUrl = new URLSearchParams(search).get('redirectTo');
  if (fromUrl) {
    if (isAllowedAppHandoffTarget(fromUrl)) { return fromUrl; }
    reportRejected('url', fromUrl);
  }

  // Guarded because this also runs in a render path. A browser with storage locked down
  // throws on access, and there is no error boundary in this app — an unguarded read here
  // blanks the screen mid-flow. Treat unreadable storage as "no saved value" and fall
  // through to the remaining candidates.
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);
  } catch {
    stored = null;
  }
  if (stored) {
    if (isAllowedAppHandoffTarget(stored)) { return stored; }
    // Reachable without an attacker: this key is shared with the Spark flows.
    reportRejected('session_storage', stored);
  }

  if (fromFlowState) {
    if (isAllowedAppHandoffTarget(fromFlowState)) { return fromFlowState; }
    reportRejected('flow_state', fromFlowState);
  }

  return undefined;
};

/** Test seam: the report-once latch is module state by design. */
export const resetRedirectReportingForTests = (): void => {
  reportedRejections.clear();
};

/**
 * The hook form, for screens mounted inside the activation provider. It supplies the two
 * inputs the lookup needs — this screen's query string and the flow's state.
 */
export const useAppSuppliedRedirectTo = (): string | undefined => {
  const { search } = useLocation();
  const { state } = useActivation();

  return appSuppliedRedirectTo({ search, fromFlowState: state.redirectTo });
};
